import { type FC, type FormEventHandler, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useRevalidator } from '@remix-run/react';

import useIsMobile from '~/hooks/useIsMobile';
import { useNectarImpression } from '~/hooks/useNectarImpression';
import type { CommunityProfileField } from '~/interfaces/community';
import type { LocationSuggestion } from '~/services/api/community/types';
import { NectarPageBlock } from '~/utils/nectarPageBlock';
import toast from '~/utils/toast';

import { BottomSheetDialog } from './ui/BottomSheetDialog';
import { Dialog } from './ui/Dialog';
import type { LocationChange } from './ui/LocationPicker';

import useBuiltInProfileFields from '../_hooks/useBuiltInProfileFields';
import useProfileQuestionAnswers from '../_hooks/useProfileQuestionAnswers';
import { useSaveOnboardingIdentityMutation } from '../_hooks/useSaveOnboardingIdentityMutation';
import type { GeolocationChange } from '../_utils/geolocationField';
import { requiredSetupPages } from '../_utils/onboardingProfileFields';
import { OnboardQuestionsPage } from '../onboard/_components/OnboardQuestionsPage';
import { RequiredBuiltInFieldsPage } from './RequiredBuiltInFieldsPage';

type RequiredProfileSetupOverlayProps = {
  fields: CommunityProfileField[];
  firstName: string;
  avatarUrl?: string;
  hasAvatar?: boolean;
  headline: string;
  location: string;
  locationSuggestion?: LocationSuggestion | null;
};

export const RequiredProfileSetupOverlay: FC<RequiredProfileSetupOverlayProps> = ({
  fields,
  firstName,
  avatarUrl,
  hasAvatar,
  headline,
  location,
  locationSuggestion,
}) => {
  const { t } = useTranslation();
  const { revalidate } = useRevalidator();
  const isMobile = useIsMobile();
  const pages = useMemo(() => requiredSetupPages(fields), [fields]);
  const builtIn = useBuiltInProfileFields(fields, { headline, location });
  const [geolocation, setGeolocation] = useState<GeolocationChange | undefined>();
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [avatarError, setAvatarError] = useState<string | undefined>();
  // A built-in only blocks setup while it is required, visible, and still empty on the member.
  const needsAvatar = builtIn.avatarField.required && hasAvatar === false;
  const needsHeadline = builtIn.headlineField.required && !headline.trim();
  const needsLocation = builtIn.locationField.required && !location.trim();
  const hasBuiltInStep = needsAvatar || needsHeadline || needsLocation;
  const [builtInDone, setBuiltInDone] = useState(false);
  const onBuiltInStep = hasBuiltInStep && !builtInDone;
  const { mutate: saveIdentity, isPending: isSavingIdentity } = useSaveOnboardingIdentityMutation();
  const [questionId, setQuestionId] = useState<string | null>(null);
  const step = Math.max(
    pages.findIndex((page) => page.some((field) => field.id === questionId)),
    0,
  );
  const currentPage = pages[step];
  const isLastQuestionPage = pages.length > 0 && step === pages.length - 1;
  const totalSteps = pages.length + (hasBuiltInStep ? 1 : 0);
  const { answers, fieldErrors, handleAnswerChange, validateAnswers, submitAnswers, isPending } =
    useProfileQuestionAnswers({
      fields,
      currentPage: currentPage ?? [],
      pageBlock: NectarPageBlock.CommunityRequiredProfileSetup,
    });
  const welcomeName = firstName.trim();
  useNectarImpression(
    { pageBlock: NectarPageBlock.CommunityRequiredProfileSetup },
    { enabled: pages.length > 0 || hasBuiltInStep },
  );

  const handleAvatarSelect = (file: File) => {
    setAvatarFile(file);
    setAvatarError(undefined);
  };

  const handleLocationChange = (change: LocationChange) => {
    builtIn.setLocation(change.location);
    setGeolocation(change.geolocation);
  };

  const handleBuiltInSubmit = () => {
    if (!builtIn.validate()) return;
    if (needsAvatar && !avatarFile) {
      setAvatarError(t('community.onboard.required'));
      return;
    }

    const onSuccess = () => {
      if (pages.length === 0) {
        revalidate();
        return;
      }
      setBuiltInDone(true);
    };
    saveIdentity(
      { ...builtIn.requestValues(geolocation), avatar: avatarFile },
      {
        onSuccess,
        onError: (error) => toast.error(error instanceof Error ? error.message : t('community.onboard.toast.error')),
      },
    );
  };

  const handleSubmit: FormEventHandler<HTMLFormElement> = (event) => {
    event.preventDefault();
    if (onBuiltInStep) {
      handleBuiltInSubmit();
      return;
    }
    if (!currentPage || !validateAnswers(currentPage)) return;

    submitAnswers(currentPage, () => {
      if (!isLastQuestionPage) {
        setQuestionId(pages[step + 1]?.[0]?.id ?? null);
        return;
      }
      revalidate();
    });
  };

  if (!onBuiltInStep && (pages.length === 0 || !currentPage)) return null;

  const form = (
    <form className="flex w-full flex-col gap-6" onSubmit={handleSubmit} noValidate>
      {onBuiltInStep ? (
        <RequiredBuiltInFieldsPage
          builtIn={builtIn}
          needsAvatar={needsAvatar}
          avatarUrl={avatarUrl}
          isDefaultAvatar={hasAvatar === false}
          avatarError={avatarError}
          onAvatarSelect={handleAvatarSelect}
          needsHeadline={needsHeadline}
          needsLocation={needsLocation}
          firstName={firstName}
          totalSteps={totalSteps}
          disabled={isSavingIdentity}
          isLastPage={pages.length === 0}
          locationSuggestion={locationSuggestion}
          onLocationChange={handleLocationChange}
        />
      ) : (
        <OnboardQuestionsPage
          fields={currentPage}
          answers={answers}
          errors={fieldErrors}
          firstName={firstName}
          currentStep={step + (hasBuiltInStep ? 1 : 0)}
          totalSteps={totalSteps}
          disabled={isPending}
          isLastPage={isLastQuestionPage}
          canGoBack={step > 0}
          title={
            welcomeName
              ? t('community.profile_setup.welcome_title', { name: welcomeName })
              : t('community.profile_setup.title')
          }
          subtitle={t('community.profile_setup.subtitle', { count: currentPage?.length ?? 0 })}
          onChange={handleAnswerChange}
          onBack={() => setQuestionId(pages[step - 1]?.[0]?.id ?? null)}
        />
      )}
    </form>
  );

  if (isMobile) {
    return (
      <BottomSheetDialog
        isOpen
        onClose={() => undefined}
        disableInitialTransition
        panelClassName="flex max-h-[90vh] flex-col overflow-y-auto rounded-t-[24px] bg-white px-6 py-8 shadow-xl"
      >
        {form}
      </BottomSheetDialog>
    );
  }

  return (
    <Dialog
      isOpen
      onClose={() => undefined}
      disableInitialTransition
      className="relative flex max-h-[calc(100dvh-2rem)] w-full max-w-[480px] flex-col overflow-y-auto rounded-[24px] bg-white px-6 py-6 shadow-xl"
    >
      {form}
    </Dialog>
  );
};
